import type { IkeRouteFieldMessage } from '@ikegps/core-api';
import { addStyles } from '../../components/theme';
import { type ModalContext, type ModalResponse, ModalService } from '../../components/modal/modal.service';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { CSS, Style } from './validation.notification.style';

addStyles(Style);

export interface ValidationNotificationConfig {
	errors?: IkeRouteFieldMessage[];
	warnings?: IkeRouteFieldMessage[];
	info?: IkeRouteFieldMessage[];
}

function groupByField(messages: IkeRouteFieldMessage[]): Map<string, IkeRouteFieldMessage[]> {
	const grouped = new Map<string, IkeRouteFieldMessage[]>();
	for (const msg of messages) {
		const field = msg.field.replace(/^config\//, '');
		if (!grouped.has(field)) {
			grouped.set(field, []);
		}
		grouped.get(field)!.push(msg);
	}
	return grouped;
}

function renderMessageGroup(field: string, messages: IkeRouteFieldMessage[]): MithrilVNode {
	return m('div', { className: CSS.section }, [
		m('span', { className: CSS.sectionTitle }, field),
		m(
			'ul',
			{ className: CSS.content },
			messages.map((msg) => m('li', { className: CSS.message }, msg.message || `${msg.field}: ${msg.codes}`))
		),
	]);
}

function renderMessages(messages: IkeRouteFieldMessage[]): MithrilVNode[] {
	const grouped = groupByField(messages);
	const children: MithrilVNode[] = [];
	grouped.forEach((msgs, field) => {
		children.push(renderMessageGroup(field, msgs));
	});
	return children;
}

function renderCategorySection(headingKey: T, messages: IkeRouteFieldMessage[], modifierClass: string): MithrilVNode {
	return m('div', { className: `${CSS.categorySection} ${modifierClass}` }, [
		m('div', { className: CSS.categoryHeading }, Translate(headingKey)),
		...renderMessages(messages),
	]);
}

function renderValidationContent(config: ValidationNotificationConfig): MithrilVNode {
	const sections: MithrilVNode[] = [];

	if (config.errors?.length > 0) {
		sections.push(
			renderCategorySection(T.FORM_IMPORT_VALIDATION_SECTION_ERRORS, config.errors, CSS.categorySectionError)
		);
	}

	if (config.warnings?.length > 0) {
		sections.push(
			renderCategorySection(T.FORM_IMPORT_VALIDATION_SECTION_WARNINGS, config.warnings, CSS.categorySectionWarning)
		);
	}

	if (config.info?.length > 0) {
		sections.push(renderCategorySection(T.FORM_IMPORT_VALIDATION_SECTION_INFO, config.info, CSS.categorySectionInfo));
	}

	return m('div', { className: CSS.container }, sections);
}

function getTitle(config: ValidationNotificationConfig): string {
	if (config.errors?.length > 0) {
		return Translate(T.FORM_IMPORT_VALIDATION_ERROR_TITLE);
	}
	return Translate(T.FORM_IMPORT_VALIDATION_WARNING_TITLE);
}

export function openValidationNotification(config: ValidationNotificationConfig): Promise<ModalResponse> {
	const hasErrors = config.errors?.length > 0;

	const modalContext: ModalContext = {
		title: getTitle(config),
		content: renderValidationContent(config),
		hasCancel: !hasErrors,
		...(!hasErrors && { okName: T.FORM_IMPORT_VALIDATION_CREATE_FORM }),
		className: CSS.modal,
	};

	return ModalService.open(modalContext);
}
